import {
  RequirementStudioViewSchema,
  type RequirementStudioInputDocument,
  type RequirementStudioRequirementRow,
  type RequirementTags,
  type RequirementStudioView,
  type SourceRef
} from "@automatry/schemas";

import type { ProjectFixtureForDisplay } from "./projectCockpitAdapter";

type FixtureInputStatus = "accepted" | "review" | "missing" | "not_produced";
type RequirementStatus = "candidate" | "approved" | "rejected" | "superseded";
type RequirementPriority = "blocking" | "default" | "option" | "ve" | "rfi";
type RequirementTagsForDisplay = NonNullable<RequirementTags>;
type RequirementSystemFacet = NonNullable<RequirementTagsForDisplay["system"]>;
type RequirementTypeFacet = NonNullable<RequirementTagsForDisplay["requirementType"]>;
type ConfidenceBand = "high" | "medium" | "low";
type RequirementSourceKind = NonNullable<RequirementTagsForDisplay["sourceKind"]>;

export interface RequirementStudioRequirementFacets {
  system?: RequirementSystemFacet;
  requirementType?: RequirementTypeFacet;
  priority?: RequirementPriority;
  confidenceBand: ConfidenceBand;
  sourceKind?: RequirementSourceKind;
}

export interface ProjectRequirementForDisplay {
  id: string;
  type: string;
  status: RequirementStatus;
  interpretedText: string;
  priority?: RequirementPriority;
  tags?: RequirementTagsForDisplay;
  productQuantity?: unknown;
  confidence: number;
  sourceRefs?: SourceRef[];
}

export interface RequirementStudioStat {
  id: string;
  label: string;
  value: string;
}

export interface RequirementStudioDisplayDocument extends RequirementStudioInputDocument {
  statusLabel: string;
  statusTone: "ok" | "review" | "missing" | "quiet";
  summary: string;
}

export interface RequirementStudioDisplayRequirement extends RequirementStudioRequirementRow {
  facets: RequirementStudioRequirementFacets;
  kindLabel: string;
  statusLabel: string;
  confidenceLabel: string;
  confidencePercent: number;
  confidenceTone: "high" | "medium" | "low";
  sourceSummary: string;
}

export interface RequirementStudioGroup {
  id: string;
  label: string;
  meta?: string;
  items: RequirementStudioDisplayRequirement[];
}

export interface RequirementStudioDisplayViewModel extends RequirementStudioView {
  documents: RequirementStudioDisplayDocument[];
  requirements: RequirementStudioDisplayRequirement[];
  stats: RequirementStudioStat[];
  groups: RequirementStudioGroup[];
  statusCounts: Record<RequirementStatus, number>;
  generatedFrom: string;
}

const statusLabels: Record<RequirementStatus, string> = {
  candidate: "Candidate",
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Superseded"
};

const documentStatus = (
  status: FixtureInputStatus
): Pick<RequirementStudioDisplayDocument, "status" | "statusLabel" | "statusTone"> => {
  if (status === "accepted") {
    return { status: "parsed", statusLabel: "Read", statusTone: "ok" };
  }

  if (status === "review") {
    return { status: "queued", statusLabel: "Queued", statusTone: "review" };
  }

  if (status === "missing") {
    return { status: "missing", statusLabel: "Missing", statusTone: "missing" };
  }

  return {
    status: "not_produced",
    statusLabel: "Not produced",
    statusTone: "quiet"
  };
};

const labelise = (value: string): string =>
  value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const publicRequirementText = (text: string): string =>
  text
    .replace(
      /;\s*Lane B must derive the required sensors, gateway\/interface, and notifications from this requirement\./gi,
      "; the controls engine must derive the self-contained gateway PC, dashboard path, alert workflow, and any required leak-sensor confirmation from this requirement."
    )
    .replace(
      /\bLane B must derive the required sensors, gateway\/interface, and notifications from this requirement\./gi,
      "The controls engine must derive the self-contained gateway PC, dashboard path, alert workflow, and any required leak-sensor confirmation from this requirement."
    )
    .replace(/\bLane A\b/g, "document extraction")
    .replace(/\bLane B\b/g, "the controls engine");

const confidenceTone = (confidence: number): ConfidenceBand => {
  if (confidence >= 0.85) {
    return "high";
  }

  if (confidence >= 0.7) {
    return "medium";
  }

  return "low";
};

const deriveSystemFacet = (
  requirement: ProjectRequirementForDisplay
): RequirementSystemFacet | undefined => requirement.tags?.system;

const deriveRequirementTypeFacet = (
  requirement: ProjectRequirementForDisplay
): RequirementTypeFacet | undefined => requirement.tags?.requirementType;

const deriveSourceKindFacet = (
  requirement: ProjectRequirementForDisplay
): RequirementSourceKind | undefined => requirement.tags?.sourceKind;

const deriveRequirementFacets = (
  requirement: ProjectRequirementForDisplay
): RequirementStudioRequirementFacets => {
  const system = deriveSystemFacet(requirement);
  const requirementType = deriveRequirementTypeFacet(requirement);
  const sourceKind = deriveSourceKindFacet(requirement);

  return {
    ...(system ? { system } : {}),
    ...(requirementType ? { requirementType } : {}),
    ...(requirement.priority ? { priority: requirement.priority } : {}),
    confidenceBand: confidenceTone(requirement.confidence),
    ...(sourceKind ? { sourceKind } : {})
  };
};

const sourceLinkLabel = (sourceRefs: readonly SourceRef[]): string => {
  const first = sourceRefs[0];

  if (!first) {
    return "No source refs on this requirement row";
  }

  return first.locator;
};

const requirementCountForDocument = (
  document: ProjectFixtureForDisplay["inputs"][number],
  requirements: readonly ProjectRequirementForDisplay[]
): number => {
  if (document.id.includes("requirements")) {
    return requirements.filter((requirement) => !requirement.type.includes("takeoff"))
      .length;
  }

  if (document.id.includes("takeoff")) {
    return requirements.filter((requirement) => requirement.type.includes("takeoff"))
      .length;
  }

  return 0;
};

const documentsFromFixture = (
  fixture: ProjectFixtureForDisplay,
  requirements: readonly ProjectRequirementForDisplay[]
): RequirementStudioDisplayDocument[] =>
  fixture.inputs.map((input) => {
    const status = documentStatus(input.status);
    const requirementCount = requirementCountForDocument(input, requirements);
    const base: RequirementStudioInputDocument = {
      id: input.id,
      kind: input.kind,
      name: input.name,
      meta: input.meta,
      status: status.status,
      sourceRefCount: input.sourceRefCount,
      requirementCount,
      dataGap:
        "Document filename, size, upload date and per-requirement sourceRef rows are not emitted by the web fixture."
    };

    return {
      ...base,
      statusLabel: status.statusLabel,
      statusTone: status.statusTone,
      summary:
        requirementCount > 0
          ? `${requirementCount} extracted requirement${requirementCount === 1 ? "" : "s"} display against this real input row.`
          : "This input is present in the fixture; no direct requirement rows are emitted against it yet."
    };
  });

const requirementRowsFromFixture = (
  requirements: readonly ProjectRequirementForDisplay[]
): RequirementStudioDisplayRequirement[] =>
  requirements.map((requirement) => {
    const sourceRefs = requirement.sourceRefs ?? [];
    const confidencePercent = Math.round(requirement.confidence * 100);
    const facets = deriveRequirementFacets(requirement);
    const row: RequirementStudioRequirementRow = {
      id: requirement.id,
      text: publicRequirementText(requirement.interpretedText),
      kind: requirement.type,
      status: requirement.status,
      confidence: requirement.confidence,
      sourceRefs,
      sourceRefCount: sourceRefs.length,
      sourceLinkStatus: sourceRefs.length > 0 ? "available" : "not_produced",
      sourceLinkLabel: sourceLinkLabel(sourceRefs)
    };

    return {
      ...row,
      facets,
      kindLabel: labelise(requirement.type),
      statusLabel: statusLabels[requirement.status],
      confidenceLabel: `${confidencePercent}% confidence`,
      confidencePercent,
      confidenceTone: confidenceTone(requirement.confidence),
      sourceSummary:
        sourceRefs.length > 0
          ? `${sourceRefs.length} source reference${sourceRefs.length === 1 ? "" : "s"}`
          : "No sourceRef payload emitted for this requirement row"
    };
  });

const countByStatus = (
  requirements: readonly ProjectRequirementForDisplay[]
): Record<RequirementStatus, number> => ({
  candidate: requirements.filter((requirement) => requirement.status === "candidate")
    .length,
  approved: requirements.filter((requirement) => requirement.status === "approved")
    .length,
  rejected: requirements.filter((requirement) => requirement.status === "rejected")
    .length,
  superseded: requirements.filter((requirement) => requirement.status === "superseded")
    .length
});

const groupsFromRequirements = (
  requirements: readonly RequirementStudioDisplayRequirement[]
): RequirementStudioGroup[] => {
  const kinds = [...new Set(requirements.map((requirement) => requirement.kind))];

  return kinds.map((kind) => ({
    id: `requirement-kind-${kind}`,
    label: labelise(kind),
    meta: `${requirements.filter((requirement) => requirement.kind === kind).length}`,
    items: requirements.filter((requirement) => requirement.kind === kind)
  }));
};

export const projectFixtureToRequirementStudioView = (
  fixture: ProjectFixtureForDisplay
): RequirementStudioDisplayViewModel => {
  const requirements = fixture.project.requirements;
  const rows = requirementRowsFromFixture(requirements);
  const documents = documentsFromFixture(fixture, requirements);
  const statusCounts = countByStatus(requirements);
  const requirementSourceRefCount = rows.reduce(
    (sum, requirement) => sum + requirement.sourceRefCount,
    0
  );
  const dataGaps = [
    ...(requirementSourceRefCount === 0
      ? [
          "Requirement sourceRefs were not emitted for these rows; the screen shows the real extracted requirements and flags source links as not yet produced."
        ]
      : []),
    "Conflict requirement ids are not emitted for this view yet.",
    "Standalone unresolved RFI ids are not emitted; open decisions remain available through the project cockpit."
  ];
  const core = RequirementStudioViewSchema.parse({
    projectId: fixture.project.projectId,
    status: fixture.project.outputBundle.status === "ready" ? "ready" : "blocked",
    requirementIds: fixture.project.requirementIds,
    conflictRequirementIds: [],
    unresolvedRfiIds: [],
    selectedRequirementId: fixture.project.requirementIds[0],
    updatedAt: fixture.fixture.generatedAt,
    inputDocuments: documents.map((document) => ({
      id: document.id,
      kind: document.kind,
      name: document.name,
      meta: document.meta,
      status: document.status,
      sourceRefCount: document.sourceRefCount,
      requirementCount: document.requirementCount,
      ...(document.dataGap ? { dataGap: document.dataGap } : {})
    })),
    requirementRows: rows.map((requirement) => ({
      id: requirement.id,
      text: requirement.text,
      kind: requirement.kind,
      status: requirement.status,
      confidence: requirement.confidence,
      sourceRefs: requirement.sourceRefs,
      sourceRefCount: requirement.sourceRefCount,
      sourceLinkStatus: requirement.sourceLinkStatus,
      sourceLinkLabel: requirement.sourceLinkLabel
    })),
    dataGaps
  });

  return {
    ...core,
    documents,
    requirements: rows,
    stats: [
      { id: "documents", label: "Documents", value: String(documents.length) },
      {
        id: "requirements",
        label: "Requirements extracted",
        value: String(requirements.length)
      },
      {
        id: "approved",
        label: "Approved by policy",
        value: String(statusCounts.approved)
      },
      {
        id: "source-links",
        label: "Requirement source links",
        value:
          requirementSourceRefCount > 0
            ? String(requirementSourceRefCount)
            : "Not yet produced"
      }
    ],
    groups: groupsFromRequirements(rows),
    statusCounts,
    generatedFrom: fixture.fixture.generatedFrom
  };
};
